import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CloudSync as CloudSyncIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  FilterListOff as FilterListOffIcon,
  WarningAmber as WarningIcon,
  FactCheck as FactCheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { attendanceAPI, pointageAPI, AttendanceDaily, AttendanceDevice } from '../../../services/api';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { PointageStatus, POINTAGE_STATUS_CONFIG } from '../../../types/pointage';
import { extractErrorMessage } from '../../../utils/errorHandler';

const HR_COLOR = '#009688';

// Statuses the biometric fill can set/adjust (present levels + absent + férié).
const FILL_STATUS_OPTIONS: PointageStatus[] = [
  'PRESENT', 'PRESENT_7H', 'PRESENT_6H', 'PRESENT_5H',
  'PRESENT_4H', 'PRESENT_3H', 'PRESENT_2H', 'PRESENT_1H', 'ABSENT', 'FERIE',
];

// Column-header filters (same pattern as Devis / Factures).
const ATT_FILTER_SCHEMA = {
  utilisateur: { type: 'text',      param: 'search' },
  date:        { type: 'dateRange', paramFrom: 'start_date', paramTo: 'end_date' },
} as const;

// Local (not UTC) YYYY-MM-DD for the <input type="date"> default.
const todayStr = (): string => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

// Shift a YYYY-MM-DD string by `delta` days, returning YYYY-MM-DD.
const shiftDay = (iso: string, delta: number): string => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

// minutes -> "9h00" / "1h05"; null/undefined -> "—"
const fmtDur = (min: number | null | undefined): string => {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
};

// Human-readable warning for an incomplete day (tooltip text).
const FLAG_LABELS: Record<string, string> = {
  sortie_manquante: 'Départ non pointé — à corriger',
  dejeuner_non_pointe: 'Déjeuner non pointé',
  en_cours: 'En cours (encore présent)',
};

const AttendanceBiometric: React.FC = () => {
  const [rows, setRows] = useState<AttendanceDaily[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [devices, setDevices] = useState<AttendanceDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillStart, setBackfillStart] = useState('');
  const [backfillEnd, setBackfillEnd] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  // Fill "Pointage du Personnel" from biometric presence.
  const [fillOpen, setFillOpen] = useState(false);
  const [fillPreview, setFillPreview] = useState<Awaited<ReturnType<typeof pointageAPI.fillFromBiometric>> | null>(null);
  const [fillPreviewLoading, setFillPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedDeviceSn, setSelectedDeviceSn] = useState<string>(''); // '' = all devices
  const [sortBy, setSortBy] = useState<'time' | 'name' | 'punch'>('time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [sortPunchIndex, setSortPunchIndex] = useState(1); // 1-based Nth pointage
  const [renameDev, setRenameDev] = useState<AttendanceDevice | null>(null);
  const [renameName, setRenameName] = useState('');
  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Column filters (Utilisateur → search, Date → start/end). Default the date
  // range to today so the page opens on today's pointages like before.
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    ATT_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  useEffect(() => {
    update('date', { from: todayStr(), to: todayStr() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toolbar search box: debounced, but writes into the same `utilisateur` filter
  // the column-header filter uses, so both controls stay in sync.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    setSearchInput((filters.utilisateur as string) || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.utilisateur]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== ((filters.utilisateur as string) || '')) {
        update('utilisateur', searchInput);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleRename = async () => {
    if (!renameDev) return;
    try {
      await attendanceAPI.renameDevice(renameDev.id, renameName.trim() || null);
      setRenameDev(null);
      await fetchDevices();
      showSuccess('Nom de l’appareil mis à jour.');
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const qp = toQueryParams();
      const res = await attendanceAPI.getDaily({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: (qp.search as string) || undefined,
        start_date: (qp.start_date as string) || undefined,
        end_date: (qp.end_date as string) || undefined,
        device_sn: selectedDeviceSn || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        punch_index: sortBy === 'punch' ? sortPunchIndex : undefined,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err: any) {
      if (!silent) {
        showError(extractErrorMessage(err));
        setRows([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters, selectedDeviceSn, sortBy, sortDir, sortPunchIndex]);

  // Click a sortable header: same column flips asc/desc, a new column starts desc.
  const toggleSort = (col: 'time' | 'name') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(0);
  };

  // Sort by the Nth pointage column (1-based). Re-clicking the same one flips dir.
  const toggleSortPunch = (index: number) => {
    if (sortBy === 'punch' && sortPunchIndex === index) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy('punch');
      setSortPunchIndex(index);
      setSortDir('desc');
    }
    setPage(0);
  };

  const fetchDevices = useCallback(async () => {
    try {
      const list = await attendanceAPI.getDevices();
      // Stable order (by id = creation order) so the device tabs never swap
      // places between refreshes.
      setDevices([...list].sort((a, b) => a.id - b.id));
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Live: poll every 5s, silently.
  useEffect(() => {
    const id = setInterval(() => {
      fetchData(true);
      fetchDevices();
    }, 5000);
    return () => clearInterval(id);
  }, [fetchData, fetchDevices]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchData(true), fetchDevices()]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleBackfill = async () => {
    try {
      setBackfilling(true);
      const start = backfillStart ? `${backfillStart} 00:00:00` : undefined;
      const end = backfillEnd ? `${backfillEnd} 23:59:59` : undefined;
      const res = await attendanceAPI.backfill({ start, end });
      setBackfillOpen(false);
      showSuccess(
        res.queued_for.length
          ? `Commande envoyée à ${res.queued_for.length} appareil(s). L'appareil renverra son historique dans ~10s (les doublons sont ignorés).`
          : 'Aucun appareil connecté à re-synchroniser.'
      );
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setBackfilling(false);
    }
  };

  // --- Fill "Pointage du Personnel" from biometric presence ---
  // Current date range (from the Du/Au filters), defaulting to today.
  const fillRange = () => ({
    start_date: (filters.date.from as string) || todayStr(),
    end_date: (filters.date.to as string) || (filters.date.from as string) || todayStr(),
  });

  // Save a "Statut Pointage" pick to the persisted draft, with an optimistic
  // update so it shows immediately (and survives the 5s poll / a refresh).
  const handleDraftChange = async (employeeId: number, date: string, status: PointageStatus) => {
    setRows((prev) => prev.map((row) =>
      row.employee_id === employeeId && row.date === date ? { ...row, draft_status: status } : row
    ));
    try {
      await pointageAPI.setPointageDraft({ employee_id: employeeId, date, status });
    } catch (err: any) {
      showError(extractErrorMessage(err));
      fetchData(true); // reload to reflect the true stored value on failure
    }
  };

  const openFill = () => {
    setFillPreview(null);
    setFillOpen(true);
  };

  const runFillPreview = useCallback(async () => {
    try {
      setFillPreviewLoading(true);
      setFillPreview(await pointageAPI.fillFromBiometric({ ...fillRange(), dry_run: true }));
    } catch (err: any) {
      showError(extractErrorMessage(err));
      setFillPreview(null);
    } finally {
      setFillPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute the preview whenever the dialog opens.
  useEffect(() => {
    if (fillOpen) runFillPreview();
  }, [fillOpen, runFillPreview]);

  const handleApplyFill = async () => {
    try {
      setApplying(true);
      const res = await pointageAPI.fillFromBiometric({ ...fillRange(), dry_run: false });
      setFillOpen(false);
      showSuccess(
        `Pointage rempli : ${res.filled_present} présents, ${res.filled_retard} retards, ${res.filled_absent} absents` +
        (res.overridden ? `, ${res.overridden} ajustés` : '') + '. ' +
        `${res.skipped_protected} congés/fériés gardés.` +
        (res.employees_without_pin ? ` ${res.employees_without_pin} employé(s) sans PIN ignoré(s).` : '')
      );
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const formatDeviceSeen = (iso: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };
  // Online = the clock contacted the server within the last 90s. It polls every
  // ~10s (ADMS Delay=10), so 90s tolerates a couple of missed beats.
  const ONLINE_WINDOW_MS = 90_000;
  const isDeviceOnline = (iso: string | null) =>
    !!iso && Date.now() - new Date(iso).getTime() < ONLINE_WINDOW_MS;

  const cell = { ...modernCellStyles, textAlign: 'center' as const };
  // Vertical separators bracketing the "P N" pointage columns, so they read as a
  // distinct block and don't blur into Date / Travaillé. (HR teal, subtle.)
  const PUNCH_DIVIDER = '2px solid rgba(0, 137, 123, 0.35)';
  // As many "Pointage N" columns as the busiest day on this page.
  const maxPunches = rows.reduce((m, r) => Math.max(m, r.punch_count), 0);
  const punchCols = Math.max(maxPunches, 1);
  const colSpan = 2 + punchCols + 3; // Utilisateur + Date + N pointages + 2 résumé (Travaillé, Retard) + Statut. (Colonne Présence masquée — repasser à +4 si réaffichée.)

  // Single source of truth for the column widths. The table is `tableLayout:
  // 'fixed'`, where any column WITHOUT a declared width only gets the slack left
  // over by the declared ones — so the "P N" columns must be sized too, or a
  // narrow viewport collapses them to ~0px and their times spill across the
  // neighbouring cell. `tableMinWidth` is derived from these (punchCols is
  // data-driven) so the table scrolls instead of crushing its columns.
  const COL_W = { user: 280, date: 120, punch: 76, worked: 100, retard: 100, statut: 150 };
  const tableMinWidth =
    COL_W.user + COL_W.date + punchCols * COL_W.punch +
    COL_W.worked + COL_W.retard + COL_W.statut;

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <FingerprintIcon sx={{ color: HR_COLOR }} />
          <Typography variant="h5">Pointage Biométrique</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained" size="small" startIcon={<FactCheckIcon />}
            onClick={openFill}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}
          >
            Insérer dans le Pointage
          </Button>
          {/* Masqué temporairement — à réactiver plus tard.
          <Button
            variant="outlined" size="small" startIcon={<CloudSyncIcon />}
            onClick={() => setBackfillOpen(true)}
            sx={{ color: HR_COLOR, borderColor: HR_COLOR }}
          >
            Re-synchroniser
          </Button>
          */}
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={handleManualRefresh} disabled={refreshing} sx={{ color: HR_COLOR }}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {devices.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {devices.length > 1 && (
            <Box
              onClick={() => { setSelectedDeviceSn(''); setPage(0); }}
              sx={{
                px: 1.5, py: 0.75, borderRadius: 1, cursor: 'pointer', userSelect: 'none',
                fontSize: '0.85rem', border: '1px solid',
                borderColor: selectedDeviceSn === '' ? HR_COLOR : 'divider',
                bgcolor: selectedDeviceSn === '' ? 'rgba(0,137,123,0.08)' : 'transparent',
                fontWeight: selectedDeviceSn === '' ? 700 : 500,
              }}
            >
              Tous les appareils
            </Box>
          )}
          {devices.map((d) => {
            const online = isDeviceOnline(d.last_seen);
            const selected = selectedDeviceSn === d.sn;
            return (
              <Box key={d.id}
                onClick={() => { setSelectedDeviceSn(d.sn); setPage(0); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 1,
                  cursor: 'pointer', userSelect: 'none', border: '1px solid',
                  borderColor: selected ? HR_COLOR : 'divider',
                  bgcolor: selected ? 'rgba(0,137,123,0.08)' : 'transparent',
                }}
              >
                <Tooltip title={online ? 'En ligne' : 'Hors ligne'}>
                  <Box component="span" sx={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    bgcolor: online ? '#2e7d32' : '#d32f2f',
                    boxShadow: `0 0 0 3px ${online ? 'rgba(46,125,50,0.18)' : 'rgba(211,47,47,0.18)'}`,
                  }} />
                </Tooltip>
                <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 400 }}>
                  <strong>{d.name || d.sn}</strong>
                  {d.last_seen ? ` · vu ${formatDeviceSeen(d.last_seen)}` : ''}
                </Typography>
                <Tooltip title="Renommer l'appareil">
                  <IconButton size="small"
                    onClick={(e) => { e.stopPropagation(); setRenameDev(d); setRenameName(d.name || ''); }}
                    sx={{ color: HR_COLOR }}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Paper>
      )}

      <Dialog open={!!renameDev} onClose={() => setRenameDev(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Renommer l'appareil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Donnez un nom lisible à la pointeuse (ex. « Pointeuse Accueil »). Il remplacera
            le numéro de série {renameDev?.sn} dans l'affichage.
          </Typography>
          <TextField
            autoFocus fullWidth size="small" label="Nom de l'appareil" value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            placeholder="ex. Pointeuse Accueil"
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDev(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleRename}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small" placeholder="Rechercher par nom ou PIN…"
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <TextField size="small" type="date" label="Du" InputLabelProps={{ shrink: true }}
          value={filters.date.from}
          onChange={(e) => update('date', { ...filters.date, from: e.target.value })} />
        <TextField size="small" type="date" label="Au" InputLabelProps={{ shrink: true }}
          value={filters.date.to}
          onChange={(e) => update('date', { ...filters.date, to: e.target.value })} />
        {(hasActive() || sortBy !== 'time' || sortDir !== 'desc') && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={() => {
              setSearchInput('');
              reset();
              // also reset sorting back to its default (most recent day first)
              setSortBy('time');
              setSortDir('desc');
              setSortPunchIndex(1);
              setPage(0);
            }}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            Effacer les filtres
          </Button>
        )}

        {/* Day navigator (‹ 📅 date ›) — jumps the Du/Au range to a single day. */}
        {(() => {
          const navDay = (filters.date.from as string) || todayStr();
          const goDay = (iso: string) => { update('date', { from: iso, to: iso }); setPage(0); };
          const label = new Date(`${navDay}T00:00:00`).toLocaleDateString('fr-FR', {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
          });
          const isToday = navDay >= todayStr();
          return (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Jour précédent">
                <IconButton size="small" onClick={() => goDay(shiftDay(navDay, -1))} sx={{ color: HR_COLOR }}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <CalendarIcon sx={{ color: HR_COLOR, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}>
                {label}
              </Typography>
              <Tooltip title={isToday ? "Aujourd'hui" : 'Jour suivant'}>
                <span>
                  <IconButton size="small" onClick={() => goDay(shiftDay(navDay, 1))} disabled={isToday} sx={{ color: HR_COLOR }}>
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })()}
      </Box>

      <TableContainer
        component={Paper}
        sx={[
          getModuleTableTheme('hr'),
          {
            // Below `tableMinWidth` the table scrolls here rather than stretching
            // the page. This is MUI's TableContainer default, but stated
            // explicitly because getModuleTableTheme() also carries an
            // `overflow: 'hidden'` (currently inert — it sits behind a descendant
            // selector that never matches this element) which would clip instead.
            overflowX: 'auto',
            // Tighter horizontal padding so the columns sit closer together.
            '& .MuiTableCell-root': {
              padding: '8px 8px !important',
              '&:first-of-type': { paddingLeft: '16px !important' },
              '&:last-child': { paddingRight: '16px !important' },
            },
          },
        ] as any}
      >
        <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...modernCellStyles, width: COL_W.user }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TableSortLabel
                    hideSortIcon
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDir : 'asc'}
                    onClick={() => toggleSort('name')}
                  >
                    Utilisateur
                  </TableSortLabel>
                  <ColumnFilter
                    type="text"
                    value={filters.utilisateur}
                    onChange={(v) => update('utilisateur', v)}
                    label="utilisateur"
                    placeholder="Nom ou PIN…"
                    color={HR_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ ...cell, width: COL_W.date }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TableSortLabel
                    hideSortIcon
                    active={sortBy === 'time'}
                    direction={sortBy === 'time' ? sortDir : 'asc'}
                    onClick={() => toggleSort('time')}
                  >
                    Date
                  </TableSortLabel>
                  <ColumnFilter
                    type="dateRange"
                    value={filters.date}
                    onChange={(v) => update('date', v)}
                    label="date"
                    color={HR_COLOR}
                  />
                </Box>
              </TableCell>
              {Array.from({ length: punchCols }).map((_, i) => (
                <TableCell key={i} align="center" sx={{
                  ...cell,
                  width: COL_W.punch,
                  ...(i === 0 ? { borderLeft: PUNCH_DIVIDER } : {}),
                  ...(i === punchCols - 1 ? { borderRight: PUNCH_DIVIDER } : {}),
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip title={`Trier par l'heure du pointage ${i + 1}`}>
                      <TableSortLabel
                        hideSortIcon
                        active={sortBy === 'punch' && sortPunchIndex === i + 1}
                        direction={sortBy === 'punch' && sortPunchIndex === i + 1 ? sortDir : 'desc'}
                        onClick={() => toggleSortPunch(i + 1)}
                      >
                        P {i + 1}
                      </TableSortLabel>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
              <Tooltip title="Heures travaillées (sessions, déjeuner exclu)"><TableCell align="center" sx={{ ...cell, width: COL_W.worked, borderLeft: PUNCH_DIVIDER }}>Travaillé</TableCell></Tooltip>
              {/* Colonne « Présence » masquée — décommenter pour la réafficher (et remettre colSpan à +4,
                  ajouter sa largeur à COL_W + tableMinWidth, sinon la colonne s'écrase sur mobile)
              <Tooltip title="Présence totale (Départ − Arrivée)"><TableCell align="center" sx={{ ...cell, width: 100 }}>Présence</TableCell></Tooltip>
              */}
              <Tooltip title="Retard selon le règlement (08:10 → -1h, 08:30 → -2h, 09:00 → -½ j)"><TableCell align="center" sx={{ ...cell, width: COL_W.retard }}>Retard</TableCell></Tooltip>
              <Tooltip title="Statut à insérer dans le Pointage du Personnel (modifiable)"><TableCell align="center" sx={{ ...cell, width: COL_W.statut }}>Statut Pointage</TableCell></Tooltip>
            </TableRow>
          </TableHead>
          <TableBody>
            {(loading && rows.length === 0) || refreshing ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} sx={{ color: HR_COLOR }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun pointage trouvé
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={`${r.device_user_id}-${r.date}`} hover>
                  <TableCell sx={{ width: COL_W.user }}>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#eceff1', flexShrink: 0 }}>
                        <PersonIcon fontSize="small" sx={{ color: '#607d8b' }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <TruncatedTextWithTooltip
                              text={r.employee_name || `Utilisateur ${r.device_user_id}`}
                              maxWidth="100%"
                            />
                          </Box>
                          {r.present && (
                            <Tooltip title="Actuellement présent">
                              <Box component="span" sx={{
                                width: 9, height: 9, borderRadius: '50%', bgcolor: '#2e7d32', flexShrink: 0,
                                boxShadow: '0 0 0 3px rgba(46,125,50,0.18)',
                              }} />
                            </Tooltip>
                          )}
                        </Box>
                        {r.employee_name && (
                          <Box sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                            PIN {r.device_user_id}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', width: COL_W.date }}>{formatDate(r.date)}</TableCell>
                  {Array.from({ length: punchCols }).map((_, i) => (
                    <TableCell key={i} sx={{
                      textAlign: 'center',
                      width: COL_W.punch,
                      color: r.punches[i] ? 'inherit' : 'text.secondary',
                      ...(i === 0 ? { borderLeft: PUNCH_DIVIDER } : {}),
                      ...(i === punchCols - 1 ? { borderRight: PUNCH_DIVIDER } : {}),
                    }}>
                      {r.punches[i] || '-'}
                    </TableCell>
                  ))}
                  {(() => {
                    const warn = (r.flags || []).filter((f) => f !== 'en_cours');
                    const enCours = (r.flags || []).includes('en_cours');
                    const warnTip = warn.map((f) => FLAG_LABELS[f] || f).join(' · ');
                    return (
                      <>
                        {/* Travaillé */}
                        <TableCell sx={{ textAlign: 'center', fontWeight: 600, borderLeft: PUNCH_DIVIDER }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            {enCours ? (
                              <Box component="span" sx={{ color: '#2e7d32', fontWeight: 500 }}>en cours</Box>
                            ) : (
                              fmtDur(r.worked_minutes)
                            )}
                            {warn.length > 0 && (
                              <Tooltip title={warnTip}>
                                <WarningIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {/* Colonne « Présence » masquée — décommenter pour la réafficher
                        <TableCell sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          {enCours ? '—' : fmtDur(r.presence_minutes)}
                        </TableCell>
                        */}
                        {/* Retard */}
                        <TableCell sx={{ textAlign: 'center', fontWeight: r.retard ? 600 : 400, color: r.retard ? '#d32f2f' : 'text.secondary' }}>
                          {r.retard || '-'}
                        </TableCell>
                        {/* Statut Pointage — editable, persisted draft; defaults to the biometric suggestion */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          {r.employee_id ? (() => {
                            const val = (r.draft_status || r.suggested_status) as PointageStatus;
                            const cfg = POINTAGE_STATUS_CONFIG[val];
                            const empId = r.employee_id;
                            return (
                              <Select
                                size="small"
                                value={val}
                                onChange={(e) =>
                                  handleDraftChange(empId, r.date, e.target.value as PointageStatus)
                                }
                                sx={{
                                  minWidth: 96, height: 30, fontSize: '0.78rem', fontWeight: 700,
                                  color: cfg.color, bgcolor: cfg.bgColor, borderRadius: 1,
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '& .MuiSelect-icon': { color: cfg.color },
                                  '& .MuiSelect-select': { py: 0.5, pl: 1.25 },
                                }}
                              >
                                {FILL_STATUS_OPTIONS.map((s) => (
                                  <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>
                                    {POINTAGE_STATUS_CONFIG[s].label}
                                  </MenuItem>
                                ))}
                              </Select>
                            );
                          })() : (
                            <Tooltip title="Utilisateur non lié à un employé — non insérable">
                              <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      </>
                    );
                  })()}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Lignes par page"
        />
      </TableContainer>

      <Dialog open={backfillOpen} onClose={() => setBackfillOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Re-synchroniser l'historique</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Demande à l'appareil de renvoyer ses pointages enregistrés pour la période choisie.
            Laissez vide pour récupérer tout l'historique encore stocké sur l'appareil.
            Les doublons sont automatiquement ignorés.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField type="date" label="Du" InputLabelProps={{ shrink: true }}
              value={backfillStart} onChange={(e) => setBackfillStart(e.target.value)} fullWidth />
            <TextField type="date" label="Au" InputLabelProps={{ shrink: true }}
              value={backfillEnd} onChange={(e) => setBackfillEnd(e.target.value)} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillOpen(false)} disabled={backfilling}>Annuler</Button>
          <Button
            variant="contained" onClick={handleBackfill} disabled={backfilling}
            startIcon={backfilling ? <CircularProgress size={16} /> : <CloudSyncIcon />}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}
          >
            Lancer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Insert into "Pointage du Personnel" from biometric presence — preview + bulk apply */}
      <Dialog open={fillOpen} onClose={() => !applying && setFillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insérer dans le Pointage du Personnel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Insère le statut de chaque employé (colonne « Statut Pointage », modifiable) dans la grille du{' '}
            <strong>Pointage du Personnel</strong> pour la période{' '}
            <strong>{formatDate(fillRange().start_date)}</strong> → <strong>{formatDate(fillRange().end_date)}</strong>{' '}
            (jours ouvrés Lun–Sam jusqu'à aujourd'hui). Les employés sans pointage sont mis Absent.
            Les congés et jours fériés déjà saisis ne sont jamais écrasés.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(0,137,123,0.04)' }}>
            {fillPreviewLoading ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                <CircularProgress size={18} sx={{ color: HR_COLOR }} /> Calcul de l'aperçu…
              </Box>
            ) : fillPreview ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: '0.9rem' }}>
                <Box>
                  <strong>{fillPreview.filled_present}</strong> présents ·{' '}
                  <strong>{fillPreview.filled_retard}</strong> retards ·{' '}
                  <strong>{fillPreview.filled_absent}</strong> absents
                  {fillPreview.filled_ferie > 0 && <> · <strong>{fillPreview.filled_ferie}</strong> fériés</>}
                  {' '}seront écrits
                </Box>
                {fillPreview.overridden > 0 && (
                  <Box sx={{ color: HR_COLOR }}>
                    dont <strong>{fillPreview.overridden}</strong> ajusté(s) manuellement
                  </Box>
                )}
                <Box sx={{ color: 'text.secondary' }}>
                  {fillPreview.skipped_protected} congés/fériés gardés · {fillPreview.days} jours ouvrés ·{' '}
                  {fillPreview.employees} employé(s) liés à un PIN
                </Box>
                {fillPreview.employees_without_pin > 0 && (
                  <Box sx={{ color: '#ed6c02', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarningIcon sx={{ fontSize: 18 }} />
                    {fillPreview.employees_without_pin} employé(s) actif(s) sans PIN pointeuse — ignoré(s).
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ color: 'text.secondary' }}>Aucun aperçu disponible.</Box>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFillOpen(false)} disabled={applying}>Annuler</Button>
          <Button
            variant="contained" onClick={handleApplyFill}
            disabled={applying || fillPreviewLoading || !fillPreview}
            startIcon={applying ? <CircularProgress size={16} /> : <FactCheckIcon />}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00796b' } }}
          >
            Insérer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
    </Box>
  );
};

export default AttendanceBiometric;
